import React, { useEffect, useMemo } from 'react';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/es/table/interface';
import styled from 'styled-components';
import { GlobalToken } from 'antd';

import { EPTableWrapper } from '@/comp';
import { permissions } from '@/constants';
import { useStores } from '@/hooks/use-stores';
import IconEdit from '@/assets/icon-edit.svg?react';

const UsersTable = observer(() => {
  const { t } = useTranslation();
  const { permissionStore, userStore, usersStore } = useStores();

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter && sorter.columnKey) {
      const sortField = sorter.columnKey;
      if (sortField === 'username' || sortField === 'email' || sortField === 'role' || sortField === 'lastUpdated') {
        usersStore.setSorting(sortField, sorter.order || 'descend');
      }
    }
  };

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const columns: ColumnsType<any> = [
    {
      title: '',
      dataIndex: 'expand',
      width: '5%',
    },
    {
      title: <p className={'bold-500'}>{t('userName')}</p>,
      dataIndex: 'username',
      key: 'username',
      width: '15%',
    },
    {
      title: <p className={'bold-500'}>{t('Email')}</p>,
      dataIndex: 'email',
      key: 'email',
      width: '22%',
    },
    {
      title: <p className={'bold-500'}>{t('phoneNumber')}</p>,
      dataIndex: 'phoneNumber',
      render: phoneNumber => phoneNumber?.fullNumber || '',
      width: '12%',
    },
    {
      title: <p className={'bold-500'}>{t('Station')}</p>,
      dataIndex: 'stations',
      width: '30%',
    },
    {
      title: <p className={'bold-500'}>{t('Role')}</p>,
      dataIndex: 'role',
      key: 'role',
      width: '15%',
    },
    {
      title: <p className={'bold-500'}>{t('Last updated')}</p>,
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      sorter: true,
      width: '15%',
      render: value => (value ? moment(value).format('MMMM D, YYYY') : ''),
    },
    {
      title: '',
      dataIndex: 'edit',
      width: '4%',
      render: (_value, userRecord) => {
        if (userRecord.key.includes('expanded')) return '';

        const isUserEditableByCurrentUser = userRecord.allStations?.some(stationId =>
          permissionStore.isAuthForStation(permissions.UPDATE_STATION_USER, stationId),
        );

        const isCurrentUserEditable =
          permissionStore.isAuthForAnyStation(permissions.UPDATE_SELF_USER) && userRecord.id === userStore.user.id;

        if (!isUserEditableByCurrentUser && !isCurrentUserEditable) return '';

        return (
          <Link to={`${userRecord.id}/edit`} state={{ userRecord }}>
            <IconEdit className={'icon-edit'} />
          </Link>
        );
      },
    },
  ];

  useEffect(() => {
    if (usersStore.sortConfig.field !== 'lastUpdated' || usersStore.sortConfig.order !== 'descend') {
      usersStore.setSorting('lastUpdated', 'descend');
    }
  }, [usersStore]);

  const dataSource = useMemo(() => {
    return usersStore.users.reduce((result, user) => {
      if (!user) return result;

      const children = isEmpty(user.stations?.slice(1))
        ? undefined
        : user.stations?.slice(1).map((station, index) => ({
            key: `expanded-${user.id}-${station?.id || index}`,
            stations: station?.fullName || '',
            role: station?.role?.name || '',
          }));

      return [
        ...result,
        {
          key: user.id,
          id: user.id,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          measurementSystem: user.measurementSystem,
          username: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          email: user.email || '',
          phoneNumber: user.phoneNumber,
          stations: user.stations?.[0]?.fullName || '',
          role: user.stations?.[0]?.role?.name || '',
          notificationsEnabled: user.notificationsEnabled,
          lastUpdated: user.lastUpdated,
          children,
          language: user.language,
          allStations: user.stations?.map(station => station.id) || [],
        },
      ];
    }, []);
  }, [usersStore.users, permissionStore, userStore]);

  const handleScrollEnd = async () => {
    await usersStore.loadMore();
  };

  return (
    <Container>
      <EPTableWrapper
        sticky={true}
        dataSource={dataSource}
        columns={columns}
        onChange={handleTableChange}
        loading={usersStore.isLoading}
        loadingRowCount={3}
        initialRowCount={25}
        rowIncrementCount={25}
        locale={{ emptyText: t('No data') }}
        pagination={false}
        viewportHeightMultiplier={0.6}
        onScrollEnd={handleScrollEnd}
        tableLayout="fixed"
        style={{ width: '100%' }}
      />
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  height: 100%;
  width: 100%;

  .truncate-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .icon-edit {
    cursor: pointer;

    &:hover {
      path {
        fill: ${({ theme: { token } }) => token.colorText};
      }
    }
  }

  .ant-table {
    border-radius: 8px;
    .ant-table-tbody > tr > td.ant-table-cell-row-hover {
      background: ${({ theme: { token } }) => token.colorBorderSecondary};
    }

    .ant-table-thead > tr > th {
      border-bottom: 2px solid ${({ theme: { token } }) => token.colorBorder};
      position: sticky;
      top: 0;
      z-index: 5;
    }

    .ant-table-thead > tr > th:hover {
      .ant-table-column-sorter {
        color: ${({ theme: { token } }) => token.colorBorder};
      }
    }

    .ant-table-column-sorter {
      color: ${({ theme: { token } }) => token.colorBorder};
    }
  }

  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:hover + tr.ant-table-row > td {
    border-top-color: ${({ theme: { token } }) => token.colorBorder};
    border-bottom-color: ${({ theme: { token } }) => token.colorBorder};
  }

  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr > td,
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr:last-child > td,
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:last-child:hover > td {
    border-top-color: ${({ theme: { token } }) => token.colorBorder};
    border-bottom-color: ${({ theme: { token } }) => token.colorBorder};
  }
`;

export default UsersTable;
